import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, Search, Filter, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EntriesPage = () => {
    const { api } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterMood, setFilterMood] = useState(null);

    useEffect(() => {
        fetchEntries();
    }, []);

    const fetchEntries = async () => {
        try {
            const response = await api.get('/journal/entries?limit=100');
            setEntries(response.data);
        } catch (error) {
            console.error('Failed to fetch entries:', error);
            toast.error('Failed to load entries');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (entryId, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!window.confirm('Are you sure you want to delete this entry?')) return;
        
        try {
            await api.delete(`/journal/entries/${entryId}`);
            setEntries(entries.filter(e => e.id !== entryId));
            toast.success('Entry deleted');
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const filteredEntries = entries.filter(entry => {
        const matchesSearch = entry.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             entry.emoji.includes(searchQuery) ||
                             (entry.song_or_genre && entry.song_or_genre.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesMood = filterMood === null || entry.mood_rating === filterMood;
        return matchesSearch && matchesMood;
    });

    const groupedEntries = filteredEntries.reduce((groups, entry) => {
        const date = new Date(entry.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(entry);
        return groups;
    }, {});

    return (
        <DashboardLayout>
            <div data-testid="entries-page" className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h1 className="font-heading text-3xl font-semibold text-foreground">
                        Your Journal Entries
                    </h1>
                    <Link to="/new-entry">
                        <Button className="rounded-full" data-testid="new-entry-link">
                            New Entry
                        </Button>
                    </Link>
                </div>

                {/* Search & Filter */}
                <Card className="glass-card">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    data-testid="search-input"
                                    placeholder="Search entries..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 h-12 rounded-xl"
                                />
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                                <Button
                                    variant={filterMood === null ? "default" : "outline"}
                                    onClick={() => setFilterMood(null)}
                                    className="rounded-full whitespace-nowrap"
                                    size="sm"
                                >
                                    All Moods
                                </Button>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(mood => (
                                    <Button
                                        key={mood}
                                        variant={filterMood === mood ? "default" : "outline"}
                                        onClick={() => setFilterMood(mood)}
                                        className="rounded-full"
                                        size="sm"
                                    >
                                        {mood}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Entries List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-pulse text-muted-foreground">Loading entries...</div>
                    </div>
                ) : filteredEntries.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-12 text-center">
                            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                                {searchQuery || filterMood 
                                    ? 'No entries match your search' 
                                    : 'No entries yet. Start journaling!'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-8">
                        {Object.entries(groupedEntries).map(([date, dayEntries]) => (
                            <motion.div
                                key={date}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <h2 className="font-heading text-lg text-muted-foreground mb-4">
                                    {date}
                                </h2>
                                <div className="space-y-4">
                                    {dayEntries.map((entry) => (
                                        <Link 
                                            key={entry.id} 
                                            to={`/entries/${entry.id}`}
                                            data-testid={`entry-${entry.id}`}
                                        >
                                            <Card className="glass-card hover:shadow-lg transition-all duration-300">
                                                <CardContent className="p-6">
                                                    <div className="flex items-start gap-4">
                                                        <div className="text-4xl">{entry.emoji}</div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <span className={`px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary`}>
                                                                    Mood: {entry.mood_rating}/10
                                                                </span>
                                                                {entry.song_or_genre && (
                                                                    <span className="text-sm text-muted-foreground">
                                                                        🎵 {entry.song_or_genre}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-foreground line-clamp-3">
                                                                {entry.content}
                                                            </p>
                                                            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                                                                <span>
                                                                    {new Date(entry.created_at).toLocaleTimeString('en-US', {
                                                                        hour: 'numeric',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </span>
                                                                {entry.location_name && (
                                                                    <span>📍 {entry.location_name}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={(e) => handleDelete(entry.id, e)}
                                                            className="text-muted-foreground hover:text-destructive"
                                                            data-testid={`delete-entry-${entry.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default EntriesPage;
